/**
 * Singleton class that is used to import the wasm and expose a reference to it that can be imported for use in other files
 */

import { Logger } from "@/logger";
import type { DrawingModule } from "./types/DrawingWeb";

export type DrawingModuleType = typeof DrawingModule;

class DrawingSingletonClass {
  private _module: DrawingModuleType | null = null;
  public readonly ASSETS_FOLDER = "/assets";

  // App and Services are created once per page lifetime and reused across all drawing opens.
  // Creating a new App on each open causes an ODA WASM heap leak (~10-40MB per cycle)
  // because ODA C++ destructors never run without an explicit .delete() call, and calling
  // .delete() on App/Services crashes ODA. Reusing is the correct ODA usage pattern.
  private _app: DrawingModule.App | null = null;
  private _services: DrawingModule.Services | null = null;
  get module(): DrawingModuleType {
    if (this._module) {
      return this._module;
    } else {
      throw new Error("Drawing module not loaded");
    }
  }

  async load() {
    //Load instance
    this._module = await this.getWebassemblyInstance();
    // Bind FS Emscripten module to module in order to access virtual filesystem
    // Read more : https://emscripten.org/docs/api_reference/Filesystem-API.html
    //this._module.FS = window.FS as DrawingModule.EmscriptenFS;
  }

  // Fonction pour obtenir l'instance WebAssembly
  async getWebassemblyInstance(): Promise<DrawingModuleType> {
    return new Promise((resolve) => {
      if (!window["Module"]) {
        window["Module"] = {
          postRun: [() => resolve(window["Module"] as DrawingModuleType)],
        };
      } else {
        const moduleRef = window["Module"];
        if (moduleRef.calledRun) {
          resolve(moduleRef as DrawingModuleType);
        } else {
          // The Emscripten glue may have created Module without a postRun array.
          moduleRef.postRun = moduleRef.postRun ?? [];
          moduleRef.postRun.push(() =>
            resolve(window["Module"] as DrawingModuleType),
          );
        }
      }
    });
  }

  /**
   * Collects diagnostic info about the cached DrawingWeb assets (SW cache + HTTP cache + perf timing).
   * Safe to call before or after clearDrawingWebCache — SW cache entries will be absent after clear,
   * but PerformanceResourceTiming entries remain until page reload.
   */
  async getDrawingWebCacheInfo(): Promise<Record<string, unknown>> {
    try {
      const cache = await caches.open("drawing-js-cache");
      const keys = await cache.keys();
      const info: Record<string, unknown> = { cachedEntries: keys.length };

      for (const req of keys) {
        const res = await cache.match(req);
        if (!res) continue;

        const label = new URL(req.url).pathname.split("/").pop() ?? req.url;
        const dateHeader = res.headers.get("date");
        info[label] = {
          url: req.url,
          status: res.status,
          date: dateHeader,
          cacheAgeSeconds: dateHeader
            ? Math.round((Date.now() - new Date(dateHeader).getTime()) / 1000)
            : null,
          age: res.headers.get("age"),
          cacheControl: res.headers.get("cache-control"),
          lastModified: res.headers.get("last-modified"),
          etag: res.headers.get("etag"),
          contentLength: res.headers.get("content-length"),
        };
      }

      // Performance timing — distinguishes HTTP disk cache / SW / network
      const wasmEntry = performance
        .getEntriesByType("resource")
        .find((e) => e.name.includes("DrawingWeb.wasm")) as
        | PerformanceResourceTiming
        | undefined;
      if (wasmEntry) {
        info["wasmLoadMs"] = Math.round(wasmEntry.duration);
        // transferSize is 0 when served from cache — decodedBodySize is the actual file
        // size regardless of delivery method, and is what distinguishes the disabled
        // (~2.4MB) build from the normal (~10.65MB) one.
        info["wasmTransferSize"] = wasmEntry.transferSize;
        info["wasmDecodedBodySize"] = wasmEntry.decodedBodySize;
        info["wasmEncodedBodySize"] = wasmEntry.encodedBodySize;
        // deliveryType (Chrome 117+): "cache" = HTTP disk/memory cache, "" = network
        info["wasmDeliveryType"] = (wasmEntry as any).deliveryType ?? "unknown";
        // workerStart > 0 = response went through a Service Worker
        info["wasmServedBySW"] = wasmEntry.workerStart > 0;
      }

      // HTTP cache: HEAD request to get current server headers without re-downloading
      const wasmUrl = keys.find((r) => r.url.includes(".wasm"))?.url;
      if (wasmUrl) {
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 2000);
          const headRes = await fetch(wasmUrl, {
            method: "HEAD",
            cache: "no-cache",
            signal: controller.signal,
          });
          clearTimeout(timeout);
          info["httpCache"] = {
            status: headRes.status, // 304 = still valid in HTTP cache
            age: headRes.headers.get("age"),
            cacheControl: headRes.headers.get("cache-control"),
            lastModified: headRes.headers.get("last-modified"),
            etag: headRes.headers.get("etag"),
            date: headRes.headers.get("date"),
          };
        } catch {
          info["httpCache"] = { error: "HEAD request failed" };
        }
      }

      return info;
    } catch {
      return { cacheInfoError: "failed to read drawing-js-cache" };
    }
  }

  /**
   * Clears the SW cache for DrawingWeb.js and .wasm.
   * Called when a disabled version is detected (writeFile1 returns empty buffer).
   */
  async clearDrawingWebCache(): Promise<void> {
    try {
      const cache = await caches.open("drawing-js-cache");
      const keys = await cache.keys();
      await Promise.all(keys.map((key) => cache.delete(key)));
      Logger.info("DrawingSingleton: drawing-js-cache cleared successfully");
    } catch {
      Logger.error("DrawingSingleton: failed to clear drawing-js-cache");
    }
  }

  safeCreateFile(name: string, arrayBuffer: ArrayBuffer) {
    Logger.debug(`DrawingSingleton : safeCreateFile ${name}`);
    try {
      window.FS.mkdir(this.ASSETS_FOLDER);
    } catch {
      // Directory already exists (e.g., SPA re-navigation)
    }

    const filePath = this.ASSETS_FOLDER + "/" + name;

    // Remove existing file if present (SPA re-navigation scenario). Not present
    // on the first open of a given drawing in this session — that's expected,
    // not a warning-worthy case.
    try {
      window.FS.unlink(filePath);
    } catch {
      // File didn't exist yet, nothing to remove.
    }
    // method is not declared in DrawingWeb but does exists
    this._module?.FS_createDataFile(
      this.ASSETS_FOLDER,
      name,
      new Uint8Array(arrayBuffer),
      true,
      true,
      true,
    );
    return filePath;
  }

  getOrCreateApp(): DrawingModule.App {
    if (!this._app) {
      this._app = new this.module.App();
    }
    return this._app;
  }

  getOrCreateServices(): DrawingModule.Services {
    if (!this._services) {
      this._services = new this.module.Services();
      this._services.initialize();
      this._services.loadModule("GripPoints");
    }
    return this._services;
  }

  /**
   * Dispose and cleanup.
   * App and Services are intentionally kept alive — they are page-lifetime singletons.
   */
  dispose(): void {
    this._module = null;
  }
}

// Export singleton instance
export const DrawingSingleton = new DrawingSingletonClass();
